// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  
  const verifyToken = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return {
        valid: false,
        message: 'Aucun token d\'authentification trouvé. Veuillez vous connecter.'
      };
    }
    
    // Décoder le token JWT pour vérifier son expiration
    try {
      // Un token JWT est au format: header.payload.signature
      const payload = token.split('.')[1];
      // Décoder la partie payload (Base64)
      const decodedPayload = JSON.parse(atob(payload));
      
      // Vérifier l'expiration du token
      const currentTime = Math.floor(Date.now() / 1000); // Temps en secondes
      if (decodedPayload.exp && decodedPayload.exp < currentTime) {
        return {
          valid: false,
          message: 'Votre session a expiré. Veuillez vous reconnecter.'
        };
      }
      
      return { valid: true };
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      return {
        valid: false,
        message: 'Token d\'authentification invalide. Veuillez vous reconnecter.'
      };
    }
  };

  // Configure axios defaults
  // axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  axios.defaults.baseURL = `${process.env.REACT_APP_API_URL_AUTHENT}`;

  // Note: in React, environment variables must be prefixed with REACT_APP_


  // Add token to all requests - et vérifier la console de débogage
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log(`Requête authentifiée vers ${config.url}`);
      } else {
        console.warn(`Requête non authentifiée vers ${config.url}`);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Handle token refresh on 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Ne pas tenter de refresh si c'est déjà une requête de refresh (évite la boucle infinie)
      const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');

      // Si erreur 401 et qu'on n'a pas encore essayé de rafraîchir le token
      if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
        originalRequest._retry = true;
        
        try {
          const refreshTokenLocal = localStorage.getItem('refreshToken');
          if (!refreshTokenLocal) {
            // Déconnexion complète si pas de refresh token
            logout();
            throw new Error('No refresh token available');
          }
          
          // Tenter de rafraîchir le token
          const response = await axios.post('/auth/refresh', {}, {
            headers: { Authorization: `Bearer ${refreshTokenLocal}` }
          });
          
          const newToken = response.data.access_token;
          localStorage.setItem('accessToken', newToken);
          setToken(newToken);
          
          // Réessayer la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('Erreur lors du rafraîchissement du token:', refreshError);
          // Déconnexion complète si le refresh token est invalide
          logout();
          return Promise.reject(refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );

  // Variable pour suivre les appels récents
  let lastFetchTime = 0;
  const FETCH_COOLDOWN = 30000; // 30 secondes de délai minimal entre les appels
  
  const getUserInfo = async () => {
    // Vérifier si on a déjà fait un appel récemment
    const now = Date.now();
    if (now - lastFetchTime < FETCH_COOLDOWN) {
      // Si un appel a été fait il y a moins de 5 secondes, retourner l'utilisateur actuel
      return currentUser;
    }
    
    // Mettre à jour le timestamp du dernier appel
    lastFetchTime = now;
    
    if (token) {
      try {
        const response = await axios.get('/auth/me');
        setCurrentUser(response.data);
        return response.data;
      } catch (error) {
        // Si erreur 401 (non autorisé) ou autre erreur liée à l'authentification
        console.error('Erreur lors de la récupération des infos utilisateur:', error);
        // Déconnexion complète pour nettoyer les tokens et données utilisateur
        logout();
        return null;
      }
    } else {
      // Si pas de token, s'assurer que l'utilisateur est bien déconnecté
      setCurrentUser(null);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      if (token) {
        await getUserInfo();
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, [token]); // Re-run when token changes

  // Timer d'inactivité : déconnexion après 1h sans activité
  useEffect(() => {
    if (!currentUser) return; // Seulement si l'utilisateur est connecté

    const IDLE_TIMEOUT = 60 * 60 * 1000; // 1 heure en millisecondes
    let idleTimer = null;

    // Fonction pour réinitialiser le timer d'inactivité
    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);

      idleTimer = setTimeout(() => {
        console.log('Déconnexion automatique : inactivité de 1 heure détectée');
        logout();
      }, IDLE_TIMEOUT);
    };

    // Événements qui indiquent une activité utilisateur
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'click', 'touchstart'];

    // Attacher les event listeners
    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    });

    // Démarrer le timer initial
    resetIdleTimer();

    // Nettoyage lors du démontage ou déconnexion
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [currentUser]);

  const login = async (username, password) => {
    // D'abord, assurons-nous de nettoyer les anciennes données
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setRefreshToken(null);
    setCurrentUser(null);
    
    // Ensuite, effectuons la connexion
    const response = await axios.post('/auth/login', { username, password });
    
    const { access_token, refresh_token, user } = response.data;
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    
    setToken(access_token);
    setRefreshToken(refresh_token);
    setCurrentUser(user);
    
    return user;
  };

  const register = async (userData) => {
    await axios.post('/auth/register', userData);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setRefreshToken(null);
    setCurrentUser(null);
  };

  const forgotPassword = async (email) => {
    await axios.post('/auth/forgot-password', { email });
  };

  const resetPassword = async (token, password) => {
    await axios.post('/auth/reset-password', { token, password });
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    currentUser,
    loading,
    token,
    isAuthenticated: !!currentUser,
    isAdmin: currentUser?.is_admin || false,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    getUserInfo,
    verifyToken,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
